import { useSearchParams } from 'react-router-dom';
import { useDrawerStore } from '../../../store';
import { useDeleteData, useFetchData, useLineStore } from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { LineModel, PageableResponse } from '../../../types';
import { Resource } from '../../../lib/enums';
import { ModuleAccess } from '../../../types/auth.model';
import { convertDate, dateTimeFormat } from '../../../lib/utils';
import { Spin } from 'antd';
import BaseTable from '../../../components/BaseTable';
import InfoModal from '../../../components/ui/modal/InfoModal';

const LineTable = ({
  api,
  moduleName,
  userRole,
  userModuleAccess,
}: {
  api: string;
  moduleName: string;
  userRole: string | undefined;
  userModuleAccess: ModuleAccess | undefined;
}) => {
  const [searchParams] = useSearchParams();
  const drawerStore = useDrawerStore();
  const lineStore = useLineStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);

  const { data, isLoading } = useFetchData<PageableResponse<LineModel>>(api, {
    resource: searchParams.get('resource') || Resource.FULL,
    search: searchParams.get('search') || '',
    length: parseInt(searchParams.get('length') || '999999'),
  });

  const {
    mutate,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteData(api);

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Section',
      dataIndex: 'section',
      render: (_text: string, record: LineModel) =>
        `${record?.section?.description === undefined ? '' : record.section?.description}`,
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: LineModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: LineModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleDataDelete = (data: LineModel) => {
    mutate({ id: data.id });
    setSpinning(true);
  };

  const handleDataEdit = (data: LineModel) => {
    lineStore.setData(data);
    drawerStore.onClose();
  };

  useEffect(() => {
    if (isSuccessDelete) setSpinning(false);
  }, [isSuccessDelete]);

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <BaseTable
        dataSource={data?.data || []}
        isLoading={isLoading || isPendingDelete}
        handleDeleteButtonClick={handleDataDelete}
        handleEditButtonClick={handleDataEdit}
        columns={columns}
        canDelete={userModuleAccess?.delete}
        canEdit={userModuleAccess?.write}
        userRole={userRole}
        module={moduleName}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default LineTable;
